// S3Integration.js
//
// Org-admin "S3 Integration" section (Bring Your Own Bucket). Admins attach their
// own S3 bucket(s), get copy-paste IAM trust + permission policies (pre-filled with
// a generated external ID + CloudGenomics' principal ARNs), paste the role ARN back,
// verify the connection, and grant/revoke per-user (bucket-level) access. All calls
// are org-scoped + authorized server-side. Rendered inside the org admin page.
import React, { useEffect, useState, useCallback } from "react";
import API_BASE from "./config";

function authHeaders() {
  return { "Content-Type": "application/json", Authorization: "Bearer " + (localStorage.getItem("idToken") || "") };
}
const api = (path, opts = {}) =>
  fetch(`${API_BASE}${path}`, { credentials: "include", headers: authHeaders(), ...opts }).then(async (r) => {
    const d = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(d.error || `HTTP ${r.status}`);
    return d;
  });

const box = { border: "1px solid #e2e8f0", borderRadius: 10, padding: 16, marginBottom: 14, background: "#fff" };
const input = { width: "100%", padding: 8, border: "1px solid #cbd5e1", borderRadius: 6, boxSizing: "border-box", marginTop: 4 };
const btn = { padding: "7px 13px", border: "1px solid #cbd5e1", borderRadius: 6, background: "#fff", color: "#1e293b", cursor: "pointer", fontSize: 13 };
const btnP = { ...btn, border: "none", background: "#2563eb", color: "#fff" };
const label = { display: "block", fontSize: 12, fontWeight: 600, color: "#475569", marginTop: 10 };
const pre = { background: "#0f172a", color: "#e2e8f0", padding: 12, borderRadius: 8, fontSize: 11.5, overflow: "auto", maxHeight: 220, whiteSpace: "pre" };

function CopyBlock({ title, obj }) {
  const [copied, setCopied] = useState(false);
  const text = JSON.stringify(obj, null, 2);
  return (
    <div style={{ marginTop: 10 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <strong style={{ fontSize: 13 }}>{title}</strong>
        <button style={btn} onClick={() => { navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 1500); }}>
          {copied ? "Copied ✓" : "Copy"}
        </button>
      </div>
      <pre style={pre}>{text}</pre>
    </div>
  );
}

export default function S3Integration() {
  const [buckets, setBuckets] = useState([]);
  const [users, setUsers] = useState([]);
  const [principals, setPrincipals] = useState([]);
  const [error, setError] = useState("");
  const [banner, setBanner] = useState("");
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ bucket_name: "", region: "us-east-1", base_prefix: "", kms_key_arn: "" });
  const [newPolicies, setNewPolicies] = useState(null); // {external_id, trust_policy, permission_policy}
  const [expanded, setExpanded] = useState(null); // bucket id whose detail is open
  const [roleArnDraft, setRoleArnDraft] = useState({});
  const [access, setAccess] = useState({}); // bucketId -> Set(user_sub)

  const load = useCallback(async () => {
    setError("");
    try {
      const [b, u] = await Promise.all([api("/org-admin/buckets"), api("/org-admin/users")]);
      setBuckets(b.buckets || []);
      setPrincipals(b.principals || []);
      setUsers(Array.isArray(u) ? u : (u.users || []));
    } catch (e) { setError(e.message); }
  }, []);
  useEffect(() => { load(); }, [load]);

  async function addBucket() {
    if (!form.bucket_name.trim()) { setError("Enter a bucket name."); return; }
    try {
      const out = await api("/org-admin/buckets", { method: "POST", body: JSON.stringify(form) });
      setNewPolicies({ id: out.id, external_id: out.external_id, trust_policy: out.trust_policy, permission_policy: out.permission_policy });
      setForm({ bucket_name: "", region: "us-east-1", base_prefix: "", kms_key_arn: "" });
      setAdding(false);
      setBanner("Bucket added. Create the IAM role in your AWS account using the policies below, then paste the role ARN and verify.");
      await load();
      setExpanded(out.id);
    } catch (e) { setError(e.message); }
  }

  async function saveRoleArn(bucket) {
    try {
      await api(`/org-admin/buckets/${bucket.id}`, { method: "PUT", body: JSON.stringify({ role_arn: roleArnDraft[bucket.id] ?? bucket.role_arn }) });
      setBanner("Saved. Click Verify to test the connection.");
      await load();
    } catch (e) { setError(e.message); }
  }
  async function verifyBucket(bucket) {
    setBanner("Verifying…"); setError("");
    try { const out = await api(`/org-admin/buckets/${bucket.id}/verify`, { method: "POST" }); setBanner("✓ " + out.message); await load(); }
    catch (e) { setError("Verify failed: " + e.message); }
  }
  async function toggleActive(bucket) {
    try { await api(`/org-admin/buckets/${bucket.id}`, { method: "PUT", body: JSON.stringify({ active: bucket.active === "Y" ? "N" : "Y" }) }); await load(); }
    catch (e) { setError(e.message); }
  }
  async function removeBucket(bucket) {
    if (!window.confirm(`Remove bucket "${bucket.bucket_name}"? Users will lose access in CloudGenomics (your data is not touched).`)) return;
    try { await api(`/org-admin/buckets/${bucket.id}`, { method: "DELETE" }); setBanner("Bucket removed."); await load(); }
    catch (e) { setError(e.message); }
  }

  async function openAccess(bucket) {
    if (expanded === bucket.id) { setExpanded(null); return; }
    setExpanded(bucket.id);
    try { const out = await api(`/org-admin/buckets/${bucket.id}/access`); setAccess((p) => ({ ...p, [bucket.id]: new Set((out.access || []).map((a) => a.user_sub)) })); }
    catch (e) { setError(e.message); }
  }
  async function toggleGrant(bucket, u) {
    const has = access[bucket.id]?.has(u.user_sub);
    try {
      if (has) await api("/org-admin/bucket-access", { method: "DELETE", body: JSON.stringify({ bucket_id: bucket.id, user_sub: u.user_sub }) });
      else await api("/org-admin/bucket-access", { method: "POST", body: JSON.stringify({ bucket_id: bucket.id, username: u.email || u.username, user_sub: u.user_sub }) });
      setAccess((p) => { const s = new Set(p[bucket.id] || []); if (has) s.delete(u.user_sub); else s.add(u.user_sub); return { ...p, [bucket.id]: s }; });
    } catch (e) { setError(e.message); }
  }

  const statusPill = (b) => {
    if (b.active !== "Y") return <span style={{ color: "#94a3b8", fontSize: 12 }}>● disabled</span>;
    if (b.last_verified_at) return <span style={{ color: "#16a34a", fontSize: 12 }}>● verified</span>;
    if (b.role_arn) return <span style={{ color: "#d97706", fontSize: 12 }}>● not verified</span>;
    return <span style={{ color: "#dc2626", fontSize: 12 }}>● needs IAM role</span>;
  };

  return (
    <div style={{ marginTop: 24 }}>
      <h3 style={{ marginBottom: 4 }}>S3 Integration (Bring Your Own Bucket)</h3>
      <p style={{ color: "#64748b", fontSize: 13, marginTop: 0 }}>
        Attach your organization's own S3 bucket(s). CloudGenomics accesses them via a cross-account IAM role you
        control (no keys shared; revoke anytime). Your data and run outputs stay in your account. Grant per-user access below.
      </p>
      {/* Always-visible setup guide so admins know what's required before they start. */}
      <div style={{ ...box, background: "#f8fafc" }}>
        <strong style={{ fontSize: 14 }}>How to connect your bucket</strong>
        <ol style={{ fontSize: 13, color: "#334155", lineHeight: 1.7, margin: "8px 0 0", paddingLeft: 20 }}>
          <li><b>Attach a bucket</b> below (name + region). We generate a unique <b>external ID</b> for it.</li>
          <li>In <b>your AWS account</b>, create an IAM role and paste in the two policies we generate:
            the <b>trust policy</b> (lets CloudGenomics assume the role, only with your external ID) and the
            <b> permission policy</b> (grants access to just that bucket). Both are shown with a Copy button right after you attach the bucket.</li>
          <li>Copy the new role's <b>ARN</b> back into the bucket's settings here and <b>Save</b>.</li>
          <li>Click <b>Test connection</b> — it must succeed before you can grant access.</li>
          <li><b>Grant users</b> access with the per-user checkboxes. Granted users can then pick this bucket on their data and launch pages.</li>
        </ol>
        <div style={{ fontSize: 12.5, color: "#64748b", marginTop: 8 }}>
          No access keys are shared — CloudGenomics assumes the IAM role you control, and you can revoke access any time by
          removing the role or deleting the bucket here. Your data stays in your AWS account.
        </div>
      </div>

      {error && <div style={{ color: "#b91c1c", marginBottom: 8 }}>{error}</div>}
      {banner && <div style={{ background: "#ecfeff", border: "1px solid #a5f3fc", borderRadius: 8, padding: 10, marginBottom: 10 }}>{banner}</div>}

      <button style={btnP} onClick={() => { setAdding(true); setNewPolicies(null); }}>➕ Attach a bucket</button>

      {adding && (
        <div style={{ ...box, marginTop: 12 }}>
          <strong>New bucket</strong>
          <label style={label}>S3 bucket name</label>
          <input style={input} value={form.bucket_name} onChange={(e) => setForm({ ...form, bucket_name: e.target.value })} placeholder="my-org-genomics-data" />
          <label style={label}>Region</label>
          <input style={input} value={form.region} onChange={(e) => setForm({ ...form, region: e.target.value })} placeholder="us-east-1" />
          <label style={label}>Base prefix (optional — restrict to a folder)</label>
          <input style={input} value={form.base_prefix} onChange={(e) => setForm({ ...form, base_prefix: e.target.value })} placeholder="e.g. cloudgenomics/" />
          <label style={label}>KMS key ARN (only if the bucket is SSE-KMS encrypted)</label>
          <input style={input} value={form.kms_key_arn} onChange={(e) => setForm({ ...form, kms_key_arn: e.target.value })} placeholder="arn:aws:kms:…" />
          <div style={{ marginTop: 12, display: "flex", gap: 8 }}>
            <button style={btnP} onClick={addBucket}>Create</button>
            <button style={btn} onClick={() => setAdding(false)}>Cancel</button>
          </div>
        </div>
      )}

      {buckets.length === 0 && !adding && <p style={{ color: "#94a3b8", fontSize: 13 }}>No buckets attached yet.</p>}

      {buckets.map((b) => (
        <div key={b.id} style={box}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div>
              <strong>{b.bucket_name}</strong> <span style={{ color: "#94a3b8", fontSize: 12 }}>· {b.region}</span> &nbsp; {statusPill(b)}
              <div style={{ fontSize: 12, color: "#64748b" }}>{b.user_count || 0} user(s) with access</div>
            </div>
            <div style={{ display: "flex", gap: 6 }}>
              <button style={btn} onClick={() => verifyBucket(b)}>Test connection</button>
              <button style={btn} onClick={() => openAccess(b)}>{expanded === b.id ? "Hide" : "Manage access"}</button>
              <button style={btn} onClick={() => toggleActive(b)}>{b.active === "Y" ? "Disable" : "Enable"}</button>
              <button style={{ ...btn, color: "#b91c1c" }} onClick={() => removeBucket(b)}>Remove</button>
            </div>
          </div>

          {expanded === b.id && (
            <div style={{ marginTop: 14, borderTop: "1px solid #eef2f7", paddingTop: 12 }}>
              {/* Setup instructions */}
              <details open={!b.last_verified_at}>
                <summary style={{ cursor: "pointer", fontWeight: 600, fontSize: 13 }}>IAM setup instructions</summary>
                <ol style={{ fontSize: 13, color: "#334155", lineHeight: 1.6 }}>
                  <li>In your AWS account, create an IAM role with the <b>trust policy</b> below (it allows CloudGenomics to assume it, only with your unique external ID).</li>
                  <li>Attach the <b>permission policy</b> below (grants access to just this bucket).</li>
                  <li>Paste the new role's ARN here and <b>Save</b>, then click <b>Test connection</b>.</li>
                </ol>
                <div style={{ fontSize: 12, color: "#64748b", display: "flex", alignItems: "center", gap: 8 }}>
                  <span>External ID: <code>{(newPolicies && newPolicies.id === b.id) ? newPolicies.external_id : b.external_id}</code></span>
                  <button
                    style={{ ...btn, padding: "2px 8px", fontSize: 11 }}
                    onClick={() => { navigator.clipboard.writeText((newPolicies && newPolicies.id === b.id) ? newPolicies.external_id : b.external_id); setBanner("External ID copied."); }}
                  >
                    Copy ID
                  </button>
                  <span style={{ color: "#b45309" }}>must match the <code>sts:ExternalId</code> in your role's trust policy exactly.</span>
                </div>
                <CopyBlock title="Trust policy" obj={(newPolicies && newPolicies.id === b.id) ? newPolicies.trust_policy : {
                  Version: "2012-10-17",
                  Statement: [{ Effect: "Allow", Principal: { AWS: principals }, Action: "sts:AssumeRole", Condition: { StringEquals: { "sts:ExternalId": b.external_id } } }],
                }} />
                <CopyBlock title="Permission policy" obj={(newPolicies && newPolicies.id === b.id) ? newPolicies.permission_policy : {
                  Version: "2012-10-17",
                  Statement: [
                    { Sid: "List", Effect: "Allow", Action: ["s3:ListBucket", "s3:GetBucketLocation"], Resource: `arn:aws:s3:::${b.bucket_name}` },
                    { Sid: "ReadWrite", Effect: "Allow", Action: ["s3:GetObject", "s3:PutObject", "s3:DeleteObject"], Resource: `arn:aws:s3:::${b.bucket_name}/*` },
                    ...(b.kms_key_arn ? [{ Sid: "KMS", Effect: "Allow", Action: ["kms:Decrypt", "kms:GenerateDataKey"], Resource: b.kms_key_arn }] : []),
                  ],
                }} />
                <label style={label}>IAM role ARN</label>
                <input style={input} defaultValue={b.role_arn || ""} onChange={(e) => setRoleArnDraft((p) => ({ ...p, [b.id]: e.target.value }))} placeholder="arn:aws:iam::123456789012:role/CloudGenomicsAccess" />
                <button style={{ ...btnP, marginTop: 8 }} onClick={() => saveRoleArn(b)}>Save role ARN</button>
              </details>

              {/* Per-user access */}
              <div style={{ marginTop: 14 }}>
                <strong style={{ fontSize: 13 }}>User access</strong>
                <div style={{ maxHeight: 220, overflow: "auto", border: "1px solid #eef2f7", borderRadius: 6, marginTop: 6 }}>
                  {users.map((u) => (
                    <label key={u.user_sub} style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 10px", borderBottom: "1px solid #f1f5f9", fontSize: 13 }}>
                      <input type="checkbox" checked={access[b.id]?.has(u.user_sub) || false} onChange={() => toggleGrant(b, u)} />
                      {u.name || u.email || u.username} <span style={{ color: "#94a3b8" }}>{u.email}</span>
                    </label>
                  ))}
                  {users.length === 0 && <div style={{ padding: 10, color: "#94a3b8", fontSize: 13 }}>No users in your organization.</div>}
                </div>
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
